import React, { SyntheticEvent, useMemo, useState } from 'react';
import { Loading } from 'glints-aries';
import { Router, withRouter } from 'next/router';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { Action, ActionFunctionAny } from 'redux-actions';

import {
  closeLoginSignUpModal,
  setLoginFormHover,
  unsetLoginFormHover,
} from 'src/actions/app';
import {
  dismissCloseableNotification,
  showCloseableNotification,
} from 'src/actions/notifications';
import { updateMeta } from 'src/actions/user/me';
import authenticate from 'src/common/authenticate';
import { TRACKING_CLASSNAMES } from 'src/common/constants';
import { Products } from 'src/common/enums';
import { useCountry } from 'src/common/hooks';
import { isApplyFlow } from 'src/common/isApplyFlow';
import { hasInternalRedirect } from 'src/common/isInternalRedirect';
import { getUtmParams } from 'src/common/links';
import { ROUTES } from 'src/common/routes';
import { trackSocialButtonClicked } from 'src/common/tracking/trackingUtils';
import { EmployersLink as SharedEmployersLink } from 'src/components/links';
import { ExternalSignInButtons } from 'src/components/LoginModal/ExternalSignInButtons';
import { OrDivider } from 'src/components/OrDivider';
import { User, UserRole } from 'src/global/models/User';
import { ReduxThunkAction, State } from 'src/global/store';
import { getConfig } from 'src/modules/ClientConfig';
import { getOnboardingRedirectURL } from 'src/modules/OnboardingGuided/utils';
import { getMissingMandatorySteps } from 'src/modules/OnboardingGuided/utils/getMissingMandatorySteps';
import {
  login,
  LoginGTMOptions,
  sendEmailVerificationToken,
} from 'src/modules/Session/Actions';
import { LoginButtonClicked as LoginButtonClickedAction } from 'src/modules/Session/GTMActions';
import {
  getIsMfaRequired,
  getIsRoleMismatch,
  getLoginError,
  getUserMeta,
} from 'src/modules/Session/Selectors';
import { getRouterQueryRedirect } from 'src/modules/SignUp/helper';
import { getFetchingMe, getUser } from 'src/selectors/user';

import EmailLoginForm from './EmailLoginForm';
import { shouldReloadAfterLogin } from './helpers';
import * as Styles from './LoginForm.sc';

const Underline = (...chunks: React.ReactNode[]) => (
  <Styles.UnderlinedText>{chunks}</Styles.UnderlinedText>
);

const EmployersLink = (...chunks: React.ReactNode[]) => (
  <SharedEmployersLink>{chunks}</SharedEmployersLink>
);

type Props = {
  loginError: any;
  me: User;
  isFetchingMe: boolean;
  userMeta: Record<string, unknown>;
  autoCloseForVerifyAlert: number;
  autoCloseForMailSentAlert: number;

  login: (
    email: string,
    password: string,
    gtmOptions: LoginGTMOptions
  ) => ReduxThunkAction<any>;
  closeLoginSignUpModal: () => ReduxThunkAction<any>;
  setLoginFormHover: ActionFunctionAny<Action<any>>;
  unsetLoginFormHover: ActionFunctionAny<Action<any>>;
  updateMeta: ActionFunctionAny<Action<any>>;
  sendEmailVerificationToken: (onSuccess?: () => void) => ReduxThunkAction<any>;
  dismissCloseableNotification: ActionFunctionAny<Action<any>>;
  showCloseableNotification: ActionFunctionAny<Action<any>>;
  LoginButtonClicked: ActionFunctionAny<Action<any>>;

  afterSignUpClicked?: () => void;

  authenticated: boolean;
  submitting: boolean;
  router: Router;
  intl: IntlShape;
  isModal: boolean;

  isMfaRequired: boolean;
  isRoleMismatch: boolean;
};

const LoginForm = ({
  authenticated,
  me,
  isFetchingMe,
  isMfaRequired,
  isRoleMismatch,
  router,
  closeLoginSignUpModal,
  userMeta,
  updateMeta,
  autoCloseForVerifyAlert,
  showCloseableNotification,
  isModal,
  afterSignUpClicked,
  LoginButtonClicked,
  autoCloseForMailSentAlert,
  loginError,
  intl,
}: Props) => {
  const country = useCountry();
  const selectMeValue = useMemo(() => {
    return me;
  }, [me]);
  const { email, role, isVerified } = selectMeValue || {};
  const [isRedirecting, setIsRedirecting] = useState(false);

  const { next, nextAs, ...queries } = router.query;
  const marketingQueryUtmParams = getUtmParams(queries);

  const pathName = router.pathname;
  const isApplyingProcess = isApplyFlow(router);
  const isShouldReloadAfterLogin = shouldReloadAfterLogin(pathName);

  const [showEmailLoginForm, setShowEmailLoginForm] = React.useState(false);

  const handleSignUpLinkClick = async (e: SyntheticEvent) => {
    e.preventDefault();

    const redirectToSignUp = () => {
      const query = getRouterQueryRedirect(router);
      const { next: _next, nextAs: _nextAs, ...queryWithoutNext } = query;
      const isPopup = router.pathname !== '/login';
      return router.push({
        pathname: '/signup',
        // Remove next query param in popup to increase onboarding conversion
        query: isPopup ? queryWithoutNext : query,
      });
    };

    if (afterSignUpClicked && typeof afterSignUpClicked === 'function') {
      /** call afterSignUpClicked after redirection */
      await redirectToSignUp();
      afterSignUpClicked();
    } else {
      redirectToSignUp();
    }

    closeLoginSignUpModal();
  };

  const renderVerifyEmailSendMessage = () => (
    <Styles.PointerHolder onClick={dismissCloseableNotification}>
      <FormattedMessage
        id="email.verification.sent"
        defaultMessage="An email verification email has been sent to your email. Please check your email inbox for instructions."
        tagName="span"
      />{' '}
      &nbsp;&nbsp;&nbsp;
    </Styles.PointerHolder>
  );

  React.useEffect(() => {
    if (authenticated) {
      LoginButtonClicked({ success: true, errors: '' });
    }
  }, [LoginButtonClicked, authenticated, role]);

  React.useEffect(() => {
    if (!authenticated) {
      return;
    }
    const sendEmailVerify = () => {
      sendEmailVerificationToken();
      showCloseableNotification({
        message: renderVerifyEmailSendMessage(),
        autoCloseInterval: autoCloseForMailSentAlert,
      });
    };

    const shouldRedirectToNextPath = () => hasInternalRedirect(router.query);

    const missingMandatoryStep = getMissingMandatorySteps(selectMeValue)[0];

    const renderVerifyEmailMessage = () => (
      <Styles.PointerHolder onClick={sendEmailVerify}>
        <FormattedMessage
          id="notification.email.verify.pointer"
          defaultMessage="Hey there! Do remember to verify your email by clicking on the email verification link in your inbox. If you don't have it, click <Underline>here</Underline> to receive the email verification email."
          values={{ Underline }}
          tagName="span"
        />
      </Styles.PointerHolder>
    );

    const redirectIfAuthenticated = async () => {
      if (isApplyingProcess || isShouldReloadAfterLogin) {
        if (isShouldReloadAfterLogin) {
          window.location.reload();
        } else {
          router.push({ pathname: next as any, query: queries }, nextAs as any);
        }
        return;
      }

      if (isFetchingMe) {
        return;
      }
      setIsRedirecting(true);

      if (isRoleMismatch || role === UserRole.COMPANY) {
        router.push('/redirect');
      } else {
        if (Object.keys(userMeta).length) {
          await updateMeta(userMeta);
        }
        if (role && role !== UserRole.CANDIDATE) {
          router.push({
            pathname: `/${ROUTES.opportunitiesJobs}`,
            query: marketingQueryUtmParams,
          });
        } else if (missingMandatoryStep) {
          router.push({
            pathname: getOnboardingRedirectURL(missingMandatoryStep),
            query: marketingQueryUtmParams,
          });
        } else if (shouldRedirectToNextPath()) {
          router.push({ pathname: next as any, query: queries }, nextAs as any);
        } else {
          router.push({
            pathname: `/${ROUTES.opportunitiesJobsRecommended}`,
            query: marketingQueryUtmParams,
          });
          if (authenticated && !isVerified) {
            showCloseableNotification({
              message: renderVerifyEmailMessage(),
              autoCloseInterval: autoCloseForVerifyAlert,
            });
          }
        }
      }

      closeLoginSignUpModal();
      setIsRedirecting(false);
    };
    if (!isRedirecting) {
      redirectIfAuthenticated();
    }
  }, [
    LoginButtonClicked,
    authenticated,
    autoCloseForMailSentAlert,
    autoCloseForVerifyAlert,
    closeLoginSignUpModal,
    country,
    email,
    isFetchingMe,
    isModal,
    isRedirecting,
    isRoleMismatch,
    isVerified,
    selectMeValue,
    role,
    router,
    showCloseableNotification,
    updateMeta,
    userMeta,
    isApplyingProcess,
    next,
    nextAs,
    queries,
    marketingQueryUtmParams,
    isShouldReloadAfterLogin,
  ]);

  React.useEffect(() => {
    if (!isMfaRequired && !isRoleMismatch) {
      return;
    }
    router.push('/redirect');
    closeLoginSignUpModal();
  }, [closeLoginSignUpModal, isMfaRequired, isRoleMismatch, router]);

  const productGtm = Products.Marketplace;
  const showLoadingScreen =
    (authenticated || isMfaRequired || isRoleMismatch || isRedirecting) &&
    !isModal;

  return (
    <div>
      <Choose>
        <When condition={showLoadingScreen}>
          <Styles.LoaderWrapper>
            <Loading />
          </Styles.LoaderWrapper>
        </When>
        <When condition={showEmailLoginForm}>
          <EmailLoginForm
            router={router}
            login={login}
            closeLoginSignUpModal={closeLoginSignUpModal}
            intl={intl}
            setLoginFormHover={setLoginFormHover}
            unsetLoginFormHover={unsetLoginFormHover}
            loginError={loginError}
            isModal={isModal}
            productGtm={productGtm}
          />
        </When>
        <Otherwise>
          <Styles.Body>
            <Styles.UpperInnerBody>
              <Styles.ContentWrapper>
                <ExternalSignInButtons
                  signInSource="login"
                  gtmPayloadString={productGtm}
                />
              </Styles.ContentWrapper>
              <Styles.ContentWrapper>
                <OrDivider
                  bold={isModal}
                  upperCase={isModal}
                  showLine={isModal}
                />
              </Styles.ContentWrapper>

              <Styles.GlintsLink
                onClick={() => {
                  trackSocialButtonClicked({
                    type: 'email',
                    page: router.asPath.includes('signup') ? 'signup' : 'login',
                  });
                  setShowEmailLoginForm(true);
                }}
                aria-label="Login with Email button"
              >
                <FormattedMessage
                  id="login.button.email.login"
                  defaultMessage="Login with Email"
                />
              </Styles.GlintsLink>
            </Styles.UpperInnerBody>

            <Styles.Divider />

            <Styles.UpperInnerBody>
              <Styles.SignUpSmallText>
                <Styles.Paragraph>
                  <span>
                    <FormattedMessage
                      id="login-page_signup"
                      defaultMessage="Don’t have Glints account?"
                    />
                  </span>
                </Styles.Paragraph>
                <Styles.GlintsLink
                  className={TRACKING_CLASSNAMES.loginSubmitButton}
                  onClick={handleSignUpLinkClick}
                >
                  <FormattedMessage id="signup" defaultMessage="Sign Up" />
                </Styles.GlintsLink>
              </Styles.SignUpSmallText>
              <Styles.SignUpSmallText>
                <FormattedMessage
                  id="login-page_employers-link"
                  defaultMessage="If you’re an employer, visit <EmployersLink>this page</EmployersLink> instead."
                  tagName="p"
                  values={{
                    EmployersLink,
                  }}
                />
              </Styles.SignUpSmallText>
            </Styles.UpperInnerBody>
          </Styles.Body>
        </Otherwise>
      </Choose>
    </div>
  );
};

const mapStateToProps = (state: State) => ({
  isMfaRequired: getIsMfaRequired(state),
  isRoleMismatch: getIsRoleMismatch(state),
  loginError: getLoginError(state),
  me: getUser(state),
  isFetchingMe: getFetchingMe(state),
  userMeta: getUserMeta(state),
  autoCloseForVerifyAlert:
    getConfig(state).AUTO_CLOSE_INTERVAL_FOR_VERIFY_ALERT,
  autoCloseForMailSentAlert: getConfig(state).AUTO_CLOSE_FOR_MAIL_SENT_ALERT,
});

const mapDispatchToProps = {
  login,
  closeLoginSignUpModal,
  setLoginFormHover,
  unsetLoginFormHover,
  updateMeta,
  dismissCloseableNotification,
  showCloseableNotification,
  LoginButtonClicked: LoginButtonClickedAction,
};

export default compose(
  injectIntl,
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(authenticate(LoginForm, true));
